import React, { useEffect, useRef, useState } from 'react';

import { formatCurrency } from '../../utils/utils';

import { LIME_TEXT } from './v9';

import type { DiscountRateProjection } from '../../utils/discountRateProjection';

// One save after the rep stops moving the slider.
export const SAVE_DEBOUNCE_MS = 500;
const STEP_PERCENT = 0.01;
// shortcut: no floor/ceiling data yet, so the range is 0 to twice the quote's rate (at least 1%); ROC-87 brings real limits.
const MIN_MAX_PERCENT = 1;

export interface DiscountRateSlider {
	/** The saved rate, as a percent. */
	baseRate: number;
	/** Client mirror of the server totals for a rate (utils/discountRateProjection). */
	project: (ratePercent: number) => DiscountRateProjection;
	onSave: (ratePercent: number) => Promise<void>;
}

/** The parts of the Customize Quote config the dock reads. */
export interface QuoteRates {
	name?: string;
	rates?: {
		standard_rates?: {
			mode?: string;
			same_for_all?: { discount_rate?: number | string | null };
		} | null;
	} | null;
}

interface DiscountRateDockProps {
	/** Customize Quote config; the enhanced format keeps the rate as a percent. */
	customRates?: QuoteRates | null;
	proposedFees: number;
	effectiveRate: number | null;
	monthlySavings: number;
	savingsIsNegative: boolean;
	onAdjust: () => void;
	/** Shown when the viewer may see ISO profit; not projected while the rate moves. */
	isoProfit?: number | null;
	/** Present when the statement's discount lines can be re-rated in place. */
	slider?: DiscountRateSlider;
}

/** Same-for-all discount rate of the active quote, or null when it varies by card or is unknown. */
export const quoteDiscountRate = (
	customRates: QuoteRates | null | undefined,
): number | null => {
	const standard = customRates?.rates?.standard_rates;
	const raw = standard?.same_for_all?.discount_rate;
	if (standard?.mode !== 'same_for_all' || raw == null || raw === '') {
		return null;
	}
	const rate = Number(raw);
	return Number.isFinite(rate) ? rate : null;
};

const round2 = (value: number) => Math.round(value * 100) / 100;

const DiscountRateDock: React.FC<DiscountRateDockProps> = ({
	customRates,
	proposedFees,
	effectiveRate,
	monthlySavings,
	savingsIsNegative,
	onAdjust,
	isoProfit,
	slider,
}) => {
	const [pending, setPending] = useState<number | null>(null);
	// Bumped when a save settles, so the "caught up" check runs again.
	const [settled, setSettled] = useState(0);
	const baseRate = slider?.baseRate ?? null;
	const rate = pending ?? baseRate ?? quoteDiscountRate(customRates);

	const onSaveRef = useRef(slider?.onSave);
	useEffect(() => {
		onSaveRef.current = slider?.onSave;
	}, [slider?.onSave]);

	// Newest rate sent; a move is compared with it, not with a base that may be stale.
	const lastSentRef = useRef<number | null>(null);
	const inFlightRef = useRef(0);
	// Saves run one after another, so an older request can never commit last.
	const queueRef = useRef<Promise<void>>(Promise.resolve());

	// The last save landed and the refetched base caught up with the slider.
	useEffect(() => {
		if (
			pending !== null &&
			baseRate !== null &&
			inFlightRef.current === 0 &&
			round2(pending) === round2(baseRate) &&
			// A stale base equal to the slider is not a caught-up base while a newer rate was sent.
			round2(baseRate) === round2(lastSentRef.current ?? baseRate)
		) {
			setPending(null);
			lastSentRef.current = null;
		}
	}, [pending, baseRate, settled]);

	useEffect(() => {
		if (pending === null || baseRate === null) return;
		if (round2(pending) === round2(lastSentRef.current ?? baseRate)) return;
		const id = setTimeout(() => {
			const value = pending;
			lastSentRef.current = value;
			inFlightRef.current += 1;
			queueRef.current = queueRef.current
				.then(() => onSaveRef.current?.(value))
				.catch(() => {
					lastSentRef.current = null;
					setPending(null);
				})
				.finally(() => {
					inFlightRef.current -= 1;
					setSettled((count) => count + 1);
				});
		}, SAVE_DEBOUNCE_MS);
		return () => clearTimeout(id);
	}, [pending, baseRate]);

	// Fixed when the dock first gets a rate, so the thumb does not re-centre after a save.
	const maxRef = useRef<number | null>(null);
	if (maxRef.current === null && baseRate !== null) {
		maxRef.current = Math.max(MIN_MAX_PERCENT, baseRate * 2);
	}

	const projected = pending !== null && slider ? slider.project(pending) : null;
	const fees = projected?.proposedTotal ?? proposedFees;
	const effective = projected?.effectiveRate ?? effectiveRate;
	const savings = projected?.monthlySavings ?? monthlySavings;
	const negative = projected ? savings < 0 : savingsIsNegative;

	const max = Math.max(maxRef.current ?? MIN_MAX_PERCENT, rate ?? 0);
	const nudge = (steps: number) =>
		rate !== null &&
		setPending(round2(Math.min(max, Math.max(0, rate + steps * STEP_PERCENT))));

	const fillPercent = rate === null ? 0 : Math.min(100, (rate / max) * 100);
	const out = (label: string, value: string, good = false) => (
		<div>
			<div className="text-[11px] text-white/[.72]">{label}</div>
			<div
				className={`whitespace-nowrap text-base font-semibold tabular-nums ${good ? LIME_TEXT : 'text-white'}`}
			>
				{value}
			</div>
		</div>
	);

	// v9 .qa-dock: fixed, because Layout's main clips position: sticky.
	return (
		<div className="fixed inset-x-0 bottom-4 z-20 mx-auto w-full max-w-[1440px] px-8">
			<div className="rounded-lg bg-primary-500 text-white shadow-[0_18px_44px_rgba(0,6,102,.45),0_0_0_4px_rgba(255,255,255,.9)]">
				<div className="grid grid-cols-[200px_minmax(0,1fr)_auto] items-center gap-6 px-5 py-3.5">
					<div>
						<div className="text-[15px] font-semibold">
							Adjust the discount rate
						</div>
						<div className="text-xs text-white/75">
							{customRates?.name ?? ''}
						</div>
					</div>
					<div className="flex items-center gap-3">
						{slider && rate !== null ? (
							<>
								<div className="relative min-w-0 flex-1">
									<div className="relative mb-1.5 mt-2.5 h-1 rounded-sm bg-white/[.22]">
										<div
											className="absolute left-0 top-0 h-1 rounded-sm bg-blue-500"
											style={{ width: `${fillPercent}%` }}
										/>
										<div
											className="absolute -top-[5px] h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 border-blue-500 bg-white shadow-[0_1px_2px_rgba(0,0,0,.1)]"
											style={{ left: `${fillPercent}%` }}
										/>
									</div>
									<input
										type="range"
										aria-label="Discount rate"
										min={0}
										max={max}
										step={STEP_PERCENT}
										value={rate}
										onChange={(event) =>
											setPending(round2(Number(event.target.value)))
										}
										className="absolute inset-x-0 -top-1 h-5 w-full cursor-pointer opacity-0"
									/>
								</div>
								<b className="w-16 text-right text-[17px] font-bold tabular-nums">
									{rate.toFixed(2)}%
								</b>
								<span className="inline-flex overflow-hidden rounded-md border border-white/35">
									<button
										type="button"
										aria-label="Lower the discount rate"
										onClick={() => nudge(-1)}
										className="h-[30px] w-7 bg-white/[.12] text-sm text-white hover:bg-white/[.22]"
									>
										−
									</button>
									<button
										type="button"
										aria-label="Raise the discount rate"
										onClick={() => nudge(1)}
										className="h-[30px] w-7 border-l border-white/35 bg-white/[.12] text-sm text-white hover:bg-white/[.22]"
									>
										+
									</button>
								</span>
							</>
						) : (
							<>
								{rate !== null && (
									<b className="w-16 text-[17px] font-bold tabular-nums">
										{rate.toFixed(2)}%
									</b>
								)}
								<button
									type="button"
									onClick={onAdjust}
									className="whitespace-nowrap rounded-md border border-white/35 bg-white/[.12] px-3 py-1 text-xs font-medium text-white hover:bg-white/[.22]"
								>
									Adjust in Customize Quote
								</button>
							</>
						)}
					</div>
					<div className="flex gap-5 border-l border-white/[.28] pl-5">
						{out('Proposed fees', formatCurrency(fees))}
						{effective !== null &&
							out('Effective rate', `${effective.toFixed(2)}%`)}
						{out(
							'Customer savings',
							`${formatCurrency(savings)} / mo`,
							!negative,
						)}
						{isoProfit != null &&
							out('ISO profit', projected ? '—' : formatCurrency(isoProfit))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default DiscountRateDock;
